import React, { useEffect, useMemo } from 'react';
import MyPortfolioCapsulesToolbar, {
    MyPortfolioCapsulesModalClassName,
} from 'widgets/myPortfolioCapsules/components/toolbar/MyPortfolioCapsulesToolbar';
import { useMyPortfolioCapsulesConfigSlice } from 'widgets/myPortfolioCapsules/store/config/configSlice';
import MyPortfolioCapsulesSettings, {
    MyPortfolioCapsulesSettingsText,
} from 'widgets/myPortfolioCapsules/components/toolbar/MyPortfolioCapsulesSettings';
import './MyPortfolioCapsulesWidget.scss';
import 'portfolio/components/SelectionStatus.scss';
import InfoBox from 'utils/components/InfoBox/InfoBox';
import ConfigSettingsModal from 'widgets/tiles/components/tile/ConfigSettingsModal';
import classNames from 'classnames';
import { useInitialiseMyPortfolioCapsulesWidget } from 'widgets/myPortfolioCapsules/hooks/useInitialiseMyPortfolioCapsulesWidget';
import { MyPortfolioCapsulesWidgetContent } from 'widgets/myPortfolioCapsules/components/MyPortfolioCapsulesWidgetContent';
import { useWidgetPortfolioSelectionStatus } from 'widgets/hooks';
import SelectionStatus from 'portfolio/components/SelectionStatus';
import { usePortfolioActiveInputUsesOrdersStream } from 'portfolio/store/hooks';
import {
    getTopic,
    isValidTopic,
} from 'widgets/myPortfolioCapsules/topics/Topic';
import { TopicType } from 'widgets/myPortfolioCapsules/topics/TopicType';
import { useIsTopicFeatureToggleEnabled } from 'widgets/myPortfolioCapsules/topics/hooks';
import { useImpactModel } from 'impactModel/use-impact-model.ts';
import { ImpactModelWarning } from 'impactModel/ImpactModelWarning.tsx';
import {
    useImpactModelExceedsLimit,
    useImpactModelWarning,
} from 'impactModel/impact-model-slice.ts';

export function MyPortfolioCapsulesWidget() {
    const { config, dispatchConfigChanged } =
        useMyPortfolioCapsulesConfigSlice();

    useInitialiseMyPortfolioCapsulesWidget();

    const { register: registerImpactModel, deregister: deregisterImpactModel } =
        useImpactModel();

    const { portfolioSelectionRequirements } =
        useWidgetPortfolioSelectionStatus();
    const activeInputUsesOrdersStream =
        usePortfolioActiveInputUsesOrdersStream();
    const isFeatureToggleEnabled = useIsTopicFeatureToggleEnabled();

    const configured = config.capsuleType && config.topic;
    const isConfiguredWithValidTopic =
        configured && isValidTopic(config.topic!);

    const impactModelWarning = useImpactModelWarning();
    const impactModelExceedsLimit = useImpactModelExceedsLimit();

    useEffect(() => {
        if (config.topic === TopicType.AdjustedCost) {
            registerImpactModel();
        } else {
            deregisterImpactModel();
        }
    }, [config.topic, deregisterImpactModel, registerImpactModel]);

    const warning = useMemo(
        () => {
            if (!configured) {
                return (
                    <InfoBox
                        info={<NotConfiguredWarning />}
                        className="not-configured-warning"
                    />
                );
            } else if (!isFeatureToggleEnabled) {
                return (
                    <InfoBox
                        info={<FeatureToggleOffWarning topic={config.topic!} />}
                        className="not-configured-warning"
                    />
                );
            }

            if (!isConfiguredWithValidTopic) {
                return (
                    <InfoBox
                        info={
                            <ConfiguredWithInvalidTopicWarning
                                topic={config.topic!}
                            />
                        }
                        className="not-configured-warning"
                    />
                );
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [config, isConfiguredWithValidTopic, isFeatureToggleEnabled]
    );

    const doNotHaveRequiredImpactModels =
        (impactModelWarning || impactModelExceedsLimit) &&
        isConfiguredWithValidTopic &&
        config.topic === 'AdjustedCost';
    const showContent =
        isConfiguredWithValidTopic &&
        isFeatureToggleEnabled &&
        activeInputUsesOrdersStream &&
        !doNotHaveRequiredImpactModels;

    return (
        <div className="MyPortfolioCapsulesWidget">
            <MyPortfolioCapsulesToolbar />
            {warning}
            {isConfiguredWithValidTopic && config.topic === 'AdjustedCost' && (
                <ImpactModelWarning message="Adjusted cost valuess may be inaccurate due to TradeSpex API error" />
            )}
            {!activeInputUsesOrdersStream &&
                isFeatureToggleEnabled &&
                isConfiguredWithValidTopic && (
                    <SelectionStatus
                        selectionRequirements={portfolioSelectionRequirements}
                        widgetName={'Portfolio Capsules Widget'}
                    />
                )}
            {showContent && <MyPortfolioCapsulesWidgetContent />}
        </div>
    );

    function NotConfiguredWarning() {
        const settingsCssClasses = classNames(
            MyPortfolioCapsulesModalClassName
        );
        return (
            <span>
                Configure a topic and capsule type in&nbsp;
                <ConfigSettingsModal
                    className={settingsCssClasses}
                    dispatcher={dispatchConfigChanged}
                    headerText={MyPortfolioCapsulesSettingsText}
                    triggerText={'Widget Settings'}
                    renderSettings={(p) => (
                        <MyPortfolioCapsulesSettings {...p} />
                    )}
                />
                .
            </span>
        );
    }

    function ConfiguredWithInvalidTopicWarning(props: { topic: TopicType }) {
        return (
            <span>
                Topic &quot;{getTopic(props.topic).description}&quot; is not
                supported in this environment.
            </span>
        );
    }

    function FeatureToggleOffWarning(props: { topic: TopicType }) {
        return (
            <span>
                Topic &quot;{getTopic(props.topic).description}&quot; is not
                enabled in this environment.
            </span>
        );
    }
}
